import { isEmpty, isNil } from 'lodash';
import ContactDrawerInformationPanel from './informationPanel/informationPanel';
import { Skeleton } from 'antd';
import { useTheme } from 'styled-components';
import { ReminderProps } from './informationPanel/types';
import { handleOpenPersonalizedInsightsNew } from './informationPanel/util';
import { SessionBoundModel } from 'redux-orm';
import { ContactConnection } from '@copilot/common/store/models/redux';
import { PipelineProps } from './contactPipeline/types';
import notificationManager from '@copilot/common/utils/notificationManager';
import { OrganizationContactQueryResult } from './data/types';

type ContactDrawerInformationPanelSmartProps = {
    /**
     * Contact id
     */
    contactId: string;
    /**
     * tags for this contact
     */
    contactTags: string[];
    /**
     * value of selected option
     */
    selectedOrgMemberId: string;
    /**
     * callback for setting tags
     */
    setContactTags: (tags: string[]) => void;
    /**
     * contact connection
     */
    contactConnection: SessionBoundModel<ContactConnection, {}> | null;
    /**
     * callback upon connected options select
     */
    handleMemberSelect: (selectedValue: string) => void;
    /**
     * the profile id of the contact
     */
    contactAlias?: string;
    /**
     * whether the current org member is an org owner
     */
    isOwner: boolean;
    isOpenProfile?: boolean;
    /**
     * Whether to show a meeting booked state for the button
     */
    isMeetingBooked: boolean;
    /**
     * Whether the meeting booked button should be loading
     */
    isMeetingLoading: boolean;
    /**
     * Callback after meeting booked is clocked
     */
    handleMeetingClick: (isBooked: boolean) => void;
    /**
     * props for set reminder functionality
     */
    reminderProps: ReminderProps;
    /**
     * Whether the set reminder button should be loading
     */
    isReminderLoading: boolean;
    /**
     * Props for pipeline view
     */
    pipelineProps: PipelineProps;
    /**
     * Id for the linkedin profile
     */
    profileId?: string;
    /**
     * Callback for saving tags
     */
    onSaveTags: (tags: string[]) => void;

    useOrgContactData: OrganizationContactQueryResult;
};

/**
 * [Smart] Contact Drawer's smart container
 * Responsible for handling data fetching and state updates
 * @constructor
 */
export default function ContactDrawerInformationPanelSmart({
    contactId,
    contactTags,
    selectedOrgMemberId,
    setContactTags,
    contactConnection,
    handleMemberSelect,
    useOrgContactData,
    reminderProps,
    isReminderLoading,
    isMeetingBooked,
    isMeetingLoading,
    handleMeetingClick,
    contactAlias,
    pipelineProps,
    isOpenProfile = false,
    isOwner,
    onSaveTags,
}: ContactDrawerInformationPanelSmartProps) {
    const {
        campaignConnections,
        onAnalyzeCommunicationStyles: handleAnalyzeCommunicationStyles,
        contact,
        loading,
        isContactTitleUpdating,
        isContactPhoneNumberUpdating,
        isContactCompanyUpdating,
        isContactEmailUpdating,
        onContactPhoneNumberUpdate,
        onContactCompanyUpdate,
        onContactEmailUpdate,
        onContactTitleUpdate,
        getCampaignConnection,
    } = useOrgContactData;


    const theme = useTheme();

    const campaignConnection = getCampaignConnection(selectedOrgMemberId);

    if (isEmpty(contactId)) return <h1>Error</h1>;

    async function onAnalyzeCommunicationStyles() {
        const campaignConnectionId = campaignConnection?.id;
        try {
            if (isNil(campaignConnectionId)) throw new Error('Missing connectionId');
            await handleAnalyzeCommunicationStyles(campaignConnectionId);
            notificationManager.showSuccessNotification({
                message: 'Communication styles analyzed',
            });
        } catch (error) {
            console.error(error);
            notificationManager.showErrorNotification({
                message: 'Failed to analyze communication styles, please try again later',
            });
        }
    }

    if (isEmpty(contactId)) return <h1>Error</h1>;

    if (loading)
        return (
            <Skeleton
                style={{
                    paddingLeft: theme['@spacer-num-sm'],
                    paddingRight: theme['@spacer-num-sm'],
                }}
                active
            />
        );

    function openPersonalizedInsights() {
        handleOpenPersonalizedInsightsNew(campaignConnection?.id ?? '');
    }

    return (
        <ContactDrawerInformationPanel
            contact={contact}
            contactTags={contactTags}
            isOpenProfile={isOpenProfile}
            campaignConnections={campaignConnections}
            selectedOrgMemberId={selectedOrgMemberId}
            contactConnection={contactConnection}
            setContactTags={setContactTags}
            handleMemberSelect={handleMemberSelect}
            prospectInfo={{
                contactCompany: {
                    value: contact.company,
                    isLoading: isContactCompanyUpdating,
                    onUpdate: (company: string) => {
                        onContactCompanyUpdate?.(company);
                    },
                },
                contactEmail: {
                    value: contact.email,
                    isLoading: isContactEmailUpdating,
                    onUpdate: (email: string) => {
                        onContactEmailUpdate?.(email);
                    },
                },
                contactTitle: {
                    value: contact.title,
                    isLoading: isContactTitleUpdating,
                    onUpdate: (title: string) => {
                        onContactTitleUpdate?.(title);
                    },
                },
                contactPhoneNumber: {
                    value: contact.phoneNumber,
                    isLoading: isContactPhoneNumberUpdating,
                    onUpdate: (phoneNumber: string) => {
                        onContactPhoneNumberUpdate?.(phoneNumber);
                    },
                },
            }}
            contactAlias={contactAlias}
            reminderProps={reminderProps}
            isReminderLoading={isReminderLoading}
            isMeetingBooked={isMeetingBooked}
            isMeetingLoading={isMeetingLoading}
            handleMeetingClick={handleMeetingClick}
            pipelineProps={pipelineProps}
            personalizedInsightsProps={{
                communicationStyles: campaignConnection?.communicationStyles,
                onViewCommunicationStyleDetails: openPersonalizedInsights,
                onAnalyzeCommunicationStyles,
                isEnabled: true,
                isShown: !isNil(campaignConnection),
            }}
            isOwner={isOwner}
            onSaveTags={onSaveTags}
        />
    );
}
